import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { UploadIcon } from "@/components/icons/action-icons";
import {
  useGetMyDocumentByIdQuery,
  useGetMyDocumentVersionsQuery,
} from "@/services/(my-space)/api.documents";
import { formatKababToCamelCase } from "@/utils/helper";
import { useLocale } from "@/hooks/useLocale";
import { Calendar, Clock, File } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useNavigate, useParams } from "react-router-dom";
import {
  DocumentDetailLayout,
  DocumentReviewDetailItem,
} from "@/components/request-detail";
import DocumentAttachments from "@/pages/(home)/_components/DocumentAttachments";
import DocumentQuickPreview from "@/pages/(home)/_components/DocumentQuickPreview";
import DocumentVersionHistory from "@/pages/(home)/_components/DocumentVersionHistory";
import { PATHS } from "@/routes/paths";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";

const ViewDocument = () => {
  const { t } = useTranslation("documents");
  const { formatDate } = useLocale();

  const navigate = useNavigate();
  const { id } = useParams();
  const { data, isLoading } = useGetMyDocumentByIdQuery(id ?? "");
  const { data: versionsData, isLoading: isLoadingVersions } =
    useGetMyDocumentVersionsQuery(id ?? "", { skip: !id });
  const document = data?.data;
  const pendingUpdate = versionsData?.data?.find((v) => v.reviewStatus === "pending");
  const updateLabel =
    document?.status === "expired" || document?.status === "expiring-soon"
      ? t("documents:renew_document")
      : document?.status === "rejected"
        ? t("documents:reupload_document")
        : t("documents:update_document");

  const uploadedDocs = [
    document?.frontSideDocumentUrl,
    document?.backSideDocumentUrl,
  ].filter(Boolean) as string[];
  const hasDocuments = uploadedDocs.length > 0;

  return (
    <DocumentDetailLayout
      title={t("documents:my_space.documents.detail_title")}
      status={document?.status}
      onBack={() => navigate(-1)}
      isLoading={isLoading}
      previewPanel={
        hasDocuments ? (
          <DocumentQuickPreview images={uploadedDocs} />
        ) : undefined
      }
      footer={
        document ? (
          <>
            <Button variant="outline" onClick={() => navigate(-1)}>
              {t("documents:my_space.ui.back")}
            </Button>
            {document.canUpdate && (
              // Same permission the upload form checks before rendering.
              <PermissionButton
                section={PLATFORM_SECTION_ENUM.DOCUMENTS}
                action="edit"
                mode="hide"
                variant="primary"
                onClick={() => navigate(PATHS.MY_SPACE.MS_DOCUMENTS_PATHS.EDIT(document.id))}
              >
                <UploadIcon className="w-4 h-4" />
                {pendingUpdate
                  ? t("documents:edit_pending_update")
                  : updateLabel}
              </PermissionButton>
            )}
          </>
        ) : undefined
      }
    >
      {document && (
        <>
          {pendingUpdate && (
            <Alert className="mt-3 mb-6">
              <Clock />
              <AlertTitle>{t("documents:update_awaiting_hr_review")}</AlertTitle>
              <AlertDescription>
                {t("documents:pending_update_description", {
                  version: pendingUpdate.version,
                })}
              </AlertDescription>
            </Alert>
          )}
          <h5 className="mt-3 mb-8 font-medium">
            {t("documents:my_space.documents.information")}
          </h5>
          <div className="flex flex-col w-full mb-8 gap-7">
            <DocumentReviewDetailItem
              icon={<File className="w-5 h-5 text-primary" />}
              color="primary/10"
              label={t("documents:my_space.documents.document_type")}
              value={t(
                `documents:${document.documentType.replace(/-/g, "_")}`,
                {
                  defaultValue: formatKababToCamelCase(document.documentType),
                },
              )}
              valueClassName="font-medium uppercase"
            />
            <DocumentReviewDetailItem
              icon={<Calendar className="w-5 h-5 text-success" />}
              color="success/10"
              label={t("documents:my_space.documents.uploaded_on")}
              value={formatDate(new Date(document.uploadedAt), "dd MMM, yyyy")}
              valueClassName="font-medium"
            />
            {document.expiryDate && (
              <DocumentReviewDetailItem
                icon={<Calendar className="w-5 h-5 text-warning" />}
                color="warning-muted/10"
                label={t("documents:my_space.documents.expiry_date")}
                value={formatDate(
                  new Date(document.expiryDate),
                  "dd MMM, yyyy",
                )}
                valueClassName="font-medium"
              />
            )}
          </div>
          <DocumentAttachments
            items={uploadedDocs.map((url) => ({
              label:
                url === document.frontSideDocumentUrl
                  ? t("documents:my_space.ui.front_side")
                  : t("documents:my_space.ui.back_side"),
              url,
            }))}
          />
          <DocumentVersionHistory
            versions={versionsData?.data}
            isLoading={isLoadingVersions}
          />
        </>
      )}
    </DocumentDetailLayout>
  );
};

export default ViewDocument;
